import { DataRoomShareableLink } from './ShareableLink';
import { ObjectValues } from 'types';

// The three secure link variants share a shape but not an outcome: a password
// request only needs the password handed over, an access request grants entry
// through the link, and an expired one cannot be approved until the link is
// restored or replaced.
export const WAITING_ROOM_REQUEST_TYPE = {
  // Someone opened a password protected link and asked for the password.
  SECURE_LINK_PASSWORD: 'secure_link_password',
  // Someone opened a live secure link they are not a recipient of.
  SECURE_LINK_ACCESS: 'secure_link_access',
  // Same, but the link they opened has since expired or been deactivated.
  SECURE_LINK_EXPIRED: 'secure_link_expired',
  // A buy-side member invited someone into their group.
  BUY_SIDE_INVITE: 'buy_side_invite',
} as const;

export type WaitingRoomRequestType = ObjectValues<
  typeof WAITING_ROOM_REQUEST_TYPE
>;

export const SECURE_LINK_REQUEST_TYPES: WaitingRoomRequestType[] = [
  WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_PASSWORD,
  WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_ACCESS,
  WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_EXPIRED,
];

// One flat row for every request type — the BE does not nest the group a
// buy-side invite points at. Which of the optional ids is filled in follows from
// `type`, and `requestingAccessTo` is the BE's already-resolved name of
// whichever of the two the request is about.
export type WaitingRoomRequest = {
  _id: string;
  dataRoomId: string;
  type: WaitingRoomRequestType;
  email: string;
  requestedAt: Date;

  userId?: string;

  dataRoomShareableLinkId?: string;
  // Everything the review drawer shows about the link — its name, what it holds
  // and the security settings rendered as pills — is nested on the row rather
  // than fetched again. Only present on the secure link request types.
  dataRoomShareableLink?: DataRoomShareableLink;
  dataRoomShareableLinkConsumerId?: string;

  dataRoomBuySideGroupId?: string;
  dataRoomMemberId?: string;

  // Secure link visitors often request access before they have an account, so
  // there is no name to show for them.
  firstName?: string;
  lastName?: string;
  companyName?: string;
  requestingAccessTo?: string;
};

export const getIsSecureLinkRequest = ({ type }: WaitingRoomRequest) =>
  SECURE_LINK_REQUEST_TYPES.includes(type);

export const getIsBuySideInviteRequest = ({ type }: WaitingRoomRequest) =>
  type === WAITING_ROOM_REQUEST_TYPE.BUY_SIDE_INVITE;

export const getIsExpiredSecureLinkRequest = ({ type }: WaitingRoomRequest) =>
  type === WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_EXPIRED;

export const getIsSecureLinkPasswordRequest = ({ type }: WaitingRoomRequest) =>
  type === WAITING_ROOM_REQUEST_TYPE.SECURE_LINK_PASSWORD;

export const getIsReviewableRequest = (request: WaitingRoomRequest) =>
  getIsBuySideInviteRequest(request) || getIsSecureLinkRequest(request);
